import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { medications } from '../services/api';

function MyClaims() {
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyClaim();
  }, []);

  const fetchMyClaim = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await medications.myClaims();
      // API returns a list, get the first one
      const claims = response.data;
      if (claims && claims.length > 0) {
        // Fetch full medication details
        const medResponse = await medications.get(claims[0].medication_id);
        const medData = medResponse.data;
        // Add review info to medication
        medData.review_id = claims[0].review_id;
        medData.claimed_at = claims[0].claimed_at;
        medData.claim_expires_at = claims[0].claim_expires_at;
        setClaim(medData);
      } else {
        setClaim(null);
      }
    } catch (err) {
      console.error('Failed to fetch claim:', err);
      // Handle error - convert to string if it's an object/array
      let errorMsg = 'Failed to load your current claim';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else {
          errorMsg = JSON.stringify(detail);
        }
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleStartReview = () => {
    if (claim) {
      navigate(`/dashboard/review/${claim.id}`);
    }
  };

  const handleRelease = async () => {
    if (!window.confirm('Are you sure you want to release this claim? You will lose access to this medication.')) {
      return;
    }

    try {
      await medications.release(claim.id);
      setClaim(null);
      alert('Claim released successfully');
    } catch (err) {
      console.error('Failed to release claim:', err);
      alert(err.response?.data?.detail || 'Failed to release claim');
    }
  };

  if (loading) {
    return <div className="loading">Loading your current claim...</div>;
  }

  if (error) {
    return (
      <div className="my-claims">
        <div className="error-message">{error}</div>
        <button onClick={fetchMyClaim} className="btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="my-claims">
        <div className="empty-state">
          <h2>📋 No Active Claim</h2>
          <p>You haven't claimed any medication yet.</p>
          <p>Go to <strong>Available Medications</strong> to claim one.</p>
          <button 
            onClick={() => navigate('/dashboard/medications')}
            className="btn-primary"
            style={{ marginTop: '16px', width: 'auto', padding: '10px 20px' }}
          >
            View Available Medications
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="my-claims">
      <h2>📝 My Current Claim</h2>
      <p className="subtitle">You're currently reviewing this medication</p>

      <div className="claim-card">
        <div className="claim-header">
          <div>
            <h3 className="claim-title">{claim.name}</h3>
            {claim.generic_name && (
              <p className="claim-generic">Generic: {claim.generic_name}</p>
            )}
          </div>
          <span className={`status-badge status-${claim.status}`}>
            {claim.status.replace('_', ' ')}
          </span>
        </div>

        {claim.drug_classes && claim.drug_classes.length > 0 && (
          <div className="claim-meta">
            <strong>Drug Classes:</strong> {claim.drug_classes.join(', ')}
          </div>
        )}

        {claim.brand_names && claim.brand_names.length > 0 && (
          <div className="claim-meta">
            <strong>Brand Names:</strong> {claim.brand_names.join(', ')}
          </div>
        )}

        {claim.claimed_at && (
          <div className="claim-meta">
            <strong>Claimed:</strong> {new Date(claim.claimed_at).toLocaleString()}
          </div>
        )}

        {claim.claim_expires_at && (
          <div className="claim-meta claim-expiry">
            <strong>Expires:</strong> {new Date(claim.claim_expires_at).toLocaleString()}
          </div>
        )}

        <div className="claim-actions">
          <button 
            onClick={handleStartReview}
            className="btn-primary"
          >
            🚀 Start Review
          </button>
          <button 
            onClick={handleRelease}
            className="btn-secondary"
          >
            Release Claim
          </button>
        </div>
      </div>
    </div>
  );
}

export default MyClaims;
